/**
 * 確認・完了の出し方をアプリの作法 (SweetAlert2) に揃えるためのヘルパー。
 *
 * 指導計画は window.confirm / alert を使っていたが、アプリ本体の既定は Swal
 * (ClassDialogContainer 等)。見た目が揃わないだけでなく、ネイティブの confirm では
 * 破壊的操作の確認ボタンを赤くできず、「この操作は取り消せません」のような
 * 副文も付けられないため、危険な操作とそうでない操作が同じ見た目になっていた。
 *
 * なお beforeunload はブラウザ仕様上ネイティブのままにする (差し替え不可)。
 */
import Swal from "sweetalert2";

/**
 * 取り返しのつかない操作の確認 (削除など)。
 * 確認ボタンを赤くし、取り消せないことを副文で明示する。
 */
export const confirmDanger = async (
  title: string,
  text: string,
  confirmButtonText = "削除します"
): Promise<boolean> => {
  const result = await Swal.fire({
    title,
    text,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "red",
    cancelButtonColor: "grey",
    confirmButtonText,
    cancelButtonText: "キャンセル",
  });
  return result.isConfirmed === true;
};

/**
 * 未保存の変更を捨ててよいかの確認。
 * 「編集を続ける」を既定の逃げ道として置き、破棄側を赤くする。
 */
export const confirmDiscard = async (
  title: string,
  text = "保存していない変更は失われます。"
): Promise<boolean> => {
  const result = await Swal.fire({
    title,
    text,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "red",
    cancelButtonColor: "grey",
    confirmButtonText: "破棄して進む",
    cancelButtonText: "編集を続ける",
  });
  return result.isConfirmed === true;
};

/**
 * 破壊的ではないが影響のある操作の確認 (公開・置き換えなど)。
 * 削除と同じ赤にすると危険度の差が伝わらないため、確認ボタンは通常色にする。
 */
export const confirmAction = async (
  title: string,
  text: string,
  confirmButtonText: string
): Promise<boolean> => {
  const result = await Swal.fire({
    title,
    text,
    icon: "question",
    showCancelButton: true,
    cancelButtonColor: "grey",
    confirmButtonText,
    cancelButtonText: "キャンセル",
  });
  return result.isConfirmed === true;
};

/** 完了の通知 */
export const notifySuccess = (title: string, text?: string): void => {
  Swal.fire({ title, text, icon: "success" });
};

/** 操作は続けられるが、そのままでは進めないことの通知 */
export const notifyWarning = (title: string, text?: string): void => {
  Swal.fire({ title, text, icon: "warning" });
};

/** 失敗の通知 */
export const notifyError = (title: string, text?: string): void => {
  Swal.fire({ title, text, icon: "error" });
};

/** 完了して次の操作を選ばせる (「開きますか？」など) */
export const confirmInfo = async (
  title: string,
  text: string,
  confirmButtonText: string
): Promise<boolean> => {
  const result = await Swal.fire({
    title,
    text,
    icon: "success",
    showCancelButton: true,
    cancelButtonColor: "grey",
    confirmButtonText,
    cancelButtonText: "閉じる",
  });
  return result.isConfirmed === true;
};
